// Global
import { Text } from '@sitecore-jss/sitecore-jss-nextjs';
// Lib
import { ComponentProps } from 'lib/component-props';
import { useTheme } from 'lib/context/ThemeContext';
import { CardComponents } from 'lib/templates/Feature.Dart.model';
import { ItemEx } from 'lib/templates/_.Sitecore.Override';
// Local
import Button from 'helpers/Button/Button';
import GoogleMaterialSymbol from 'helpers/GoogleMaterialSymbol/GoogleMaterialSymbol';
import ImageWrapper from 'helpers/ImageWrapper/ImageWrapper';
import LinkWrapper from 'helpers/LinkWrapper/LinkWrapper';
import tokensTheme from 'src/theme/token-files/brands';
import SVG from 'helpers/SVG/SVG';
import { stripHtml } from 'lib/utils/regex';
import LegalDisclaimer from 'helpers/LegalDisclaimer/LegalDisclaimer';
import fallback from 'lib/fallback/fallback';
import { tailwindVariants } from './SideBySideTailwind';

// Types
interface ObjectStructure {
  bgcolor: string;
}
interface backgroundColor {
  backgroundColor: string;
}
export type CategoryCard = ItemEx & CardComponents.CategoryCard.CategoryCardItem;
export type CategoryCardProps = ComponentProps & CardComponents.CategoryCard.CategoryCardsList;
export type CategoryCardItem = backgroundColor & CardComponents.CategoryCard.CategoryCardItem;
type AlignCTA = 'BottomCenter' | 'TopRight' | undefined;
type Grid = '2column' | '3column' | '4column' | undefined;

// Add fallback component variant color
const fallbackComponentVariantColor = fallback?.componentVariants?.value;
const fallbackComponentVariantType = fallback?.componentVariants?.type;

const CategoryCardListingSideBySide = ({ ...props }: CategoryCardProps) => {
  const {
    title,
    description,
    primaryCTA,
    enablePattern,
    disclaimerText,
    primaryCTAType,
    primaryCTAColor,
  } = props?.fields || {};
  const { alignCTA, grid } = props?.params || '';
  const { componentName, dataSource } = props?.rendering || {};
  const HideCardCTA = props?.fields?.hideCTA?.value;

  /**
   * Filter background colors for the category card background.
   * 1. Get theme name
   * 2. Get Colors
   * 3. filter out components-category-card-color-category
   */
  const { themeName, tailwindThemeName } = useTheme();
  // Get colors for specific theme
  const variantGroup = tokensTheme?.[tailwindThemeName]?.colors;
  // Filter out background
  const filterBackground = (obj: typeof variantGroup) => {
    const result = [];
    for (const key in obj) {
      if (key.includes('components-category-card-color-category')) {
        result.push(`fill-${key}`);
      }
    }
    return result;
  };
  // Call the function
  const variants = filterBackground(variantGroup);
  // map trhough the key and create new array
  const result: ObjectStructure[] = variants.map((item) => ({ bgcolor: item }));
  // Combine Background Color and Category Props into a single array
  const combinedArray = props?.fields?.categoryCards?.map((category, index) => ({
    ...category,
    backgroundColor: result[index % result.length]?.bgcolor || '',
  }));
  const {
    base,
    headerContainer,
    headerTitleText,
    headerDescriptionText,
    headerTitleContainer,
    ctaContainer,
    card,
    cardContainer,
    cardWrapper,
    cardContent,
    cardContentWrapper,
    cardMediaWrapper,
    cardMediaContainer,
    cardIcon,
    cardTitle,
    cardSVG,
    legalDisclaimerText,
  } = tailwindVariants({
    alignCTA: alignCTA as AlignCTA,
    /* eslint-disable  @typescript-eslint/ban-ts-comment */
    // @ts-ignore
    brand: themeName as string,
    grid: grid as Grid,
  });

  return (
    <>
      <div className={base()}>
        {title?.value && (
          <div className={headerContainer()}>
            {title?.value && (
              <div className={headerTitleContainer()}>
                {title?.value && <Text className={headerTitleText()} field={title} tag="p" />}
                {description?.value && (
                  <Text
                    className={headerDescriptionText()}
                    encode={false}
                    field={description}
                    tag="p"
                  />
                )}
              </div>
            )}
            {primaryCTA?.value?.href != '' && alignCTA == 'TopRight' && (
              <div className={ctaContainer()}>
                <Button
                  href={primaryCTA?.value?.href}
                  linktype={primaryCTA?.value?.linktype}
                  label={primaryCTA?.value.text}
                  title={primaryCTA?.value?.title}
                  tag="a"
                  target={primaryCTA?.value?.target}
                  // The design requires an outline CTA but field name is primaryCTA,
                  // so for that we have added a fallback as an outline value,
                  // so if there is no value in sitecore field, it will take the outline value.
                  type={primaryCTAType?.value || fallbackComponentVariantType}
                  color={primaryCTAColor?.value || fallbackComponentVariantColor}
                  gtmEvent={{
                    event: 'cta_click',
                    type: 'primary',
                    'gtm.element.dataset.gtmLinkUrl': primaryCTA?.value?.href,
                    'gtm.element.dataset.gtmLinkName': primaryCTA?.value?.text,
                    'gtm.element.dataset.gtmDatasourceId': dataSource,
                    'gtm.element.dataset.gtmComponentName': componentName,
                  }}
                />
              </div>
            )}
          </div>
        )}
        <div className={cardContainer()}>
          {combinedArray?.map((cards: CategoryCardItem) => (
            <>
              <LinkWrapper
                aria-label={`${stripHtml(cards?.fields?.title?.value as string)}${
                  cards?.fields?.description?.value ? ` - ${cards?.fields?.description?.value}` : ''
                }`}
                className={cardWrapper()}
                field={cards?.fields?.ctaLink}
                suppressLinkText
                ignoreEE
              >
                <div className={`${card()}`}>
                  <div className={cardContentWrapper()}>
                    <div className={cardContent()}>
                      <div className={cardTitle()}>
                        <Text encode={false} field={cards?.fields?.title} tag="span" />
                      </div>
                      {!HideCardCTA && (
                        <>
                          <GoogleMaterialSymbol className={cardIcon()} icon="arrow_forward" />
                        </>
                      )}
                    </div>
                  </div>
                  {cards?.fields?.image?.value?.src && (
                    <div className={`${cardMediaWrapper()}`}>
                      {enablePattern?.value && (
                        <>
                          <SVG
                            svg={`CategoryCard/Breakpoint=Large,Brand=${themeName}`}
                            className={`${cardSVG({
                              size: 'small',
                            })} ${cards?.backgroundColor}`}
                          />
                          <SVG
                            svg={`CategoryCard/Breakpoint=Large,Brand=${themeName}`}
                            className={`${cardSVG({
                              size: 'large',
                            })} ${cards?.backgroundColor}`}
                          />
                        </>
                      )}
                      <div className={cardMediaContainer()}>
                        {cards?.fields?.image && (
                          <ImageWrapper field={cards?.fields?.image} layout="responsive" />
                        )}
                      </div>
                    </div>
                  )}
                </div>
              </LinkWrapper>
            </>
          ))}
        </div>
        {primaryCTA?.value?.href != '' && alignCTA == 'BottomCenter' && (
          <div className={ctaContainer()}>
            <Button
              href={primaryCTA?.value?.href}
              linktype={primaryCTA?.value?.linktype}
              label={primaryCTA?.value?.text}
              title={primaryCTA?.value?.title}
              tag="a"
              target={primaryCTA?.value?.target}
              // The design requires an outline CTA but field name is primaryCTA,
              // so for that we have added a fallback as an outline value,
              // so if there is no value in sitecore field, it will take the outline value.
              type={primaryCTAType?.value || fallbackComponentVariantType}
              color={primaryCTAColor?.value || fallbackComponentVariantColor}
              gtmEvent={{
                event: 'cta_click',
                type: 'primary',
                'gtm.element.dataset.gtmLinkUrl': primaryCTA?.value?.href,
                'gtm.element.dataset.gtmLinkName': primaryCTA?.value?.text,
                'gtm.element.dataset.gtmDatasourceId': dataSource,
                'gtm.element.dataset.gtmComponentName': componentName,
              }}
            />
          </div>
        )}
        {disclaimerText?.value != '' && (
          <LegalDisclaimer
            disclaimerText={disclaimerText}
            disclaimerClasses={legalDisclaimerText()}
          />
        )}
      </div>
    </>
  );
};
export default CategoryCardListingSideBySide;
